"use client";

/**
 * Pose la mesure des contacts, en un seul endroit.
 *
 * 1. Fige la PROVENANCE de la visite a la premiere page vue (referent, utm).
 * 2. Compte les clics telephone et WhatsApp par DELEGATION d'evenement.
 *
 * Pourquoi la delegation plutot qu'un `onClick` sur chaque lien : les liens
 * `tel:` et WhatsApp sont dissemines dans la barre mobile, le pied de page, le
 * bouton flottant, les pages vehicule, les articles de blog… Un ecouteur unique
 * sur le document les couvre TOUS, y compris ceux qui seront ajoutes plus tard.
 * Aucun risque d'oublier un lien, aucun composant a modifier.
 *
 * `capture: true` : l'ecouteur passe AVANT tout `onClick` qui appellerait
 * `stopPropagation` (le bouton WhatsApp flottant, par exemple, ouvre une
 * fenetre par JS).
 */
import { useEffect } from "react";
import { captureAttribution, trackIntent } from "@/lib/attribution-client";

const isWhatsAppHref = (href: string) =>
  href.includes("wa.me") || href.includes("whatsapp.com");

export function AttributionProvider(): null {
  useEffect(() => {
    captureAttribution();

    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      const href = anchor?.getAttribute("href") ?? "";

      if (href.startsWith("tel:")) {
        trackIntent("telephone");
        return;
      }
      if (href && isWhatsAppHref(href)) {
        trackIntent("whatsapp");
        return;
      }

      // Le bouton WhatsApp flottant n'est pas un <a> : il ouvre la fenetre en
      // JavaScript. On le reconnait a son libelle accessible.
      const button = target?.closest?.("button") as HTMLButtonElement | null;
      const label = button?.getAttribute("aria-label")?.toLowerCase() ?? "";
      if (label.includes("whatsapp")) trackIntent("whatsapp");
    };

    document.addEventListener("click", onClick, { capture: true });
    return () =>
      document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
}

export default AttributionProvider;
